import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CreditCard, CheckCircle, ShoppingBag } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (v) => `$ ${(Number(v) || 0).toFixed(2)}`;

export default function OrderBalancePay() {
  const { collection, orderId } = useParams();
  const [searchParams] = useSearchParams();
  const isSuccess = searchParams.get('success') === 'true';

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/public/order/${collection}/${orderId}`);
      setOrder(data);
    } catch {
      toast.error('Pedido não encontrado');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [collection, orderId]);

  const payCard = async () => {
    setPaying(true);
    try {
      const { data } = await axios.post(`${API}/public/order/balance-checkout`, {
        collection, order_id: orderId, origin_url: window.location.origin,
      });
      if (data.url) window.location.assign(data.url);
      else { toast.error('Erro ao processar'); setPaying(false); }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao processar');
      setPaying(false);
    }
  };

  const payZelle = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API}/public/order/zelle-receipt?collection=${collection}&order_id=${orderId}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Comprovante enviado! Pagamento confirmado.');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao enviar comprovante');
    } finally {
      setUploading(false);
    }
  };

  const fullyPaid = order && order.payment_status === 'pago';
  const dueNow = order ? order.amount_due_now : 0;
  const phaseLabel = order?.payment_mode === 'deposit'
    ? (order.deposit_status === 'pago' ? 'saldo' : 'sinal (50%)')
    : 'total';

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white border border-[#E6E2D8] rounded-2xl p-6" data-testid="order-balance-page">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-lg bg-[#E85D04] text-white flex items-center justify-center font-bold">TK</div>
          <span className="font-['Outfit'] font-bold text-[#1A1A1A]">Tia Karol Sports</span>
        </div>

        {loading ? (
          <p className="text-[#73706A] text-center py-8" data-testid="order-balance-loading">Carregando...</p>
        ) : !order ? (
          <p className="text-[#73706A] text-center py-8">Pedido não encontrado.</p>
        ) : (
          <>
            <h1 className="text-xl font-['Outfit'] font-bold text-[#1A1A1A] flex items-center gap-2" data-testid="order-balance-title">
              <ShoppingBag size={22} className="text-[#E85D04]" /> Pagamento do pedido
            </h1>
            <p className="text-sm text-[#73706A] mt-1">{order.name}</p>

            <div className="mt-4 bg-[#F5F1E8] rounded-lg p-4 text-sm space-y-1">
              <p className="font-medium text-[#1A1A1A]">{order.product_name}</p>
              <div className="flex justify-between text-[#73706A]"><span>Valor total</span><span>{money(order.total)}</span></div>
              {order.payment_mode === 'deposit' && (
                <>
                  <div className="flex justify-between text-[#73706A]"><span>Sinal (50%)</span><span>{money(order.deposit_amount)} · {order.deposit_status}</span></div>
                  <div className="flex justify-between text-[#73706A]"><span>Saldo</span><span>{money(order.balance_amount)} · {order.balance_status}</span></div>
                </>
              )}
            </div>

            {isSuccess || fullyPaid ? (
              <div className="mt-5 bg-green-50 border border-green-200 text-green-900 rounded-xl p-5 flex items-start gap-3" data-testid="order-balance-success">
                <CheckCircle size={24} weight="fill" className="flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold">{fullyPaid ? 'Pedido totalmente pago!' : 'Pagamento recebido!'}</p>
                  <p className="text-sm mt-1">Obrigado. Em breve você receberá a confirmação.</p>
                </div>
              </div>
            ) : (
              <div className="mt-5 space-y-3">
                <div className="flex justify-between text-[#1A1A1A] font-bold text-base" data-testid="order-balance-due">
                  <span>Pagar agora ({phaseLabel})</span>
                  <span>{money(dueNow)}</span>
                </div>
                <Button onClick={payCard} disabled={paying} className="w-full bg-[#E85D04] hover:bg-[#DC2F02] text-white" data-testid="order-balance-pay-card">
                  <CreditCard size={18} className="mr-2" />
                  {paying ? 'Processando...' : `Pagar ${money(dueNow * 1.05)} com cartão`}
                </Button>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-900">
                  <p className="font-semibold">Ou pague via Zelle</p>
                  <p className="mt-1">Envie <strong>{money(dueNow)}</strong> para <strong>5618596830</strong> e anexe o comprovante:</p>
                  <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" onChange={(e) => payZelle(e.target.files?.[0])} disabled={uploading} className="mt-2 block w-full text-sm border border-blue-200 rounded p-2 file:mr-2 file:px-3 file:py-1 file:rounded file:border-0 file:bg-[#0077B6] file:text-white" data-testid="order-balance-zelle-upload" />
                  {uploading && <p className="mt-1">Enviando...</p>}
                </div>
                <p className="text-xs text-[#73706A] text-center">Pagamento seguro</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
